import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Sparkles,
  CheckCircle,
  AlertCircle,
  Building2,
  MapPin,
  Calendar
} from 'lucide-react'
import { Button, Card } from '../components/common'
import useSurveyStore from '../stores/surveyStore'
import useCategoryStore from '../stores/categoryStore'
import { formatDate } from '../utils/helpers'
import { downloadPhotosAsZip } from '../services/exportService'

export default function AnalysisResultPage() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [exporting, setExporting] = useState(false)
  const [exportType, setExportType] = useState(null)

  const currentSurvey = useSurveyStore(state => state.currentSurvey)
  const currentPhotos = useSurveyStore(state => state.currentPhotos)
  const loading = useSurveyStore(state => state.loading)
  const loadSurvey = useSurveyStore(state => state.loadSurvey)
  const clearCurrentSurvey = useSurveyStore(state => state.clearCurrentSurvey)

  const categories = useCategoryStore(state => state.categories)
  const loadCategories = useCategoryStore(state => state.loadCategories)
  const getCategory = useCategoryStore(state => state.getCategory)

  useEffect(() => {
    if (id) {
      loadSurvey(id)
      loadCategories()
    }

    return () => {
      clearCurrentSurvey()
    }
  }, [id, loadSurvey, loadCategories, clearCurrentSurvey])

  const handleDownloadPhotos = async () => {
    if (!currentSurvey || currentPhotos.length === 0) return
    setExporting(true)
    setExportType('photos')
    try {
      await downloadPhotosAsZip(currentSurvey, currentPhotos, categories)
    } catch (error) {
      console.error('Failed to download photos:', error)
    } finally {
      setExporting(false)
      setExportType(null)
    }
  }

  if (!currentSurvey && !loading) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Survey not found</h2>
        <Button variant="primary" onClick={() => navigate('/surveys')}>
          View All Surveys
        </Button>
      </div>
    )
  }

  if (loading || !currentSurvey) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner w-8 h-8" />
      </div>
    )
  }

  const analysisResult = currentSurvey.analysisResult

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate(`/survey/${id}`)}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to survey
        </button>
        <h1 className="text-2xl font-bold text-slate-900">
          Analysis Results
        </h1>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2 text-sm text-slate-500">
          <span className="flex items-center gap-1">
            <Building2 className="w-4 h-4" />
            {currentSurvey.propertyName}
          </span>
          <span className="flex items-center gap-1">
            <MapPin className="w-4 h-4" />
            {currentSurvey.postcode}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {formatDate(currentSurvey.createdAt)}
          </span>
        </div>
      </div>

      {/* Export buttons */}
      <div className="flex flex-wrap gap-3">
        <Button
          variant="primary"
          icon="Download"
          onClick={handleDownloadPhotos}
          loading={exporting && exportType === 'photos'}
          disabled={exporting || currentPhotos.length === 0}
        >
          Download All Photos
        </Button>
      </div>

      {/* Analysis content */}
      {analysisResult ? (
        <div className="space-y-6">
          {/* Overall summary */}
          {analysisResult.overallSummary && (
            <Card>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 mb-2">
                    Overall Summary
                  </h2>
                  <p className="text-slate-600 whitespace-pre-wrap">
                    {analysisResult.overallSummary}
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Category results */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">
              Analysis by Category
            </h2>

            {Object.entries(analysisResult.categories || {}).map(([categoryCode, result]) => {
              const category = getCategory(categoryCode)
              if (!category) return null

              const categoryPhotos = currentPhotos.filter(p => p.categoryCode === categoryCode)

              return (
                <Card key={categoryCode}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        {category.name}
                      </h3>
                      <p className="text-sm text-slate-500">
                        {categoryPhotos.length} photos analysed
                      </p>
                    </div>
                    {result.success ? (
                      <div className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="w-5 h-5" />
                        <span className="text-sm font-medium">Analysed</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-amber-600">
                        <AlertCircle className="w-5 h-5" />
                        <span className="text-sm font-medium">Partial</span>
                      </div>
                    )}
                  </div>

                  {result.analysis && (
                    <div className="prose prose-slate max-w-none">
                      <p className="text-slate-600 whitespace-pre-wrap">
                        {result.analysis}
                      </p>
                    </div>
                  )}

                  {result.error && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-600 text-sm">{result.error}</p>
                    </div>
                  )}

                  {/* Photo thumbnails */}
                  {categoryPhotos.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="text-sm font-medium text-slate-700 mb-2">Photos:</p>
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {categoryPhotos.map((photo) => (
                          <img
                            key={photo.id}
                            src={photo.imageUrl}
                            alt=""
                            className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              )
            })}
          </div>

          {/* Recommendations */}
          {analysisResult.recommendations && (
            <Card>
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                Recommendations
              </h2>
              <div className="prose prose-slate max-w-none">
                <p className="text-slate-600 whitespace-pre-wrap">
                  {analysisResult.recommendations}
                </p>
              </div>
            </Card>
          )}
        </div>
      ) : (
        <Card className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">
            No Analysis Available
          </h3>
          <p className="text-slate-500 mb-4">
            This survey hasn't been analysed yet.
          </p>
          <Button
            variant="primary"
            onClick={() => navigate(`/survey/${id}`)}
          >
            Go to Survey
          </Button>
        </Card>
      )}
    </div>
  )
}
